import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { resolveImageUrl } from "@/lib/resolveImageUrl";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Package, Save, Search, Store, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeAssignment = (assignment = {}) => ({
  assigned: Boolean(assignment.assigned),
  discount: assignment.discount || 0,
  custom_price: assignment.custom_price ?? null,
});

export default function AdminClientProducts() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [initialAssignments, setInitialAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchData();
  }, [restaurantId]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [restaurantRes, assignmentsRes, countRes] = await Promise.all([
        axios.get(`${API}/admin/restaurants/${restaurantId}`),
        axios.get(`${API}/admin/restaurants/${restaurantId}/assignments`),
        axios.get(`${API}/admin/products/count`, { params: { search: "" } }),
      ]);

      const totalProducts = countRes?.data?.count ?? 0;
      const productsRes = await axios.get(`${API}/admin/products`, {
        params: { skip: 0, limit: Math.max(50, totalProducts), search: "" },
      });

      const mappedAssignments = {};
      for (const assignment of assignmentsRes.data || []) {
        mappedAssignments[assignment.product_id] = normalizeAssignment({
          assigned: true,
          discount: assignment.discount,
          custom_price: assignment.custom_price,
        });
      }

      setRestaurant(restaurantRes.data);
      setAllProducts(productsRes.data || []);
      setAssignments(mappedAssignments);
      setInitialAssignments(JSON.parse(JSON.stringify(mappedAssignments)));
    } catch {
      toast.error("No se pudieron cargar los datos del restaurante");
      navigate("/admin/clients");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(
    () =>
      allProducts.filter((product) => {
        if (!search) return true;
        return [product.name, product.code, product.category]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(search));
      }),
    [allProducts, search],
  );

  const assignedCount = Object.values(assignments).filter((assignment) => assignment.assigned).length;
  const hasChanges = JSON.stringify(assignments) !== JSON.stringify(initialAssignments);

  const toggleProduct = (productId) => {
    setAssignments((prev) => {
      const current = normalizeAssignment(prev[productId]);

      if (current.assigned) {
        return {
          ...prev,
          [productId]: normalizeAssignment({ assigned: false, discount: 0, custom_price: null }),
        };
      }

      return {
        ...prev,
        [productId]: normalizeAssignment({
          assigned: true,
          discount: current.discount,
          custom_price: current.custom_price,
        }),
      };
    });
  };

  const updateAssignmentField = (productId, field, value) => {
    setAssignments((prev) => ({
      ...prev,
      [productId]: normalizeAssignment({
        ...prev[productId],
        assigned: true,
        [field]: value,
      }),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const productIds = new Set([...Object.keys(assignments), ...Object.keys(initialAssignments)]);

      for (const productId of productIds) {
        const before = normalizeAssignment(initialAssignments[productId]);
        const after = normalizeAssignment(assignments[productId]);

        if (after.assigned && !before.assigned) {
          await axios.post(`${API}/admin/assign-product`, {
            restaurant_id: restaurantId,
            product_id: productId,
            discount: after.discount,
            custom_price: after.custom_price,
          });
          continue;
        }

        if (!after.assigned && before.assigned) {
          await axios.delete(`${API}/admin/assign-product/${restaurantId}/${productId}`);
          continue;
        }

        if (
          after.assigned &&
          before.assigned &&
          (after.discount !== before.discount || after.custom_price !== before.custom_price)
        ) {
          await axios.put(`${API}/admin/assign-product/${restaurantId}/${productId}`, {
            discount: after.discount,
            custom_price: after.custom_price,
          });
        }
      }

      setInitialAssignments(JSON.parse(JSON.stringify(assignments)));
      toast.success("Catálogo actualizado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudieron guardar los cambios");
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="restaurant-products-page">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/admin/clients")}
              data-testid="restaurant-products-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                  <Store className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">Catálogo del restaurante</h1>
                  <p className="text-sm text-slate-500">
                    Asigna productos, descuentos y precios personalizados por restaurante.
                  </p>
                </div>
              </div>

              {restaurant && (
                <div className="flex flex-wrap gap-2 text-sm" data-testid="restaurant-products-header">
                  <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700" data-testid="restaurant-products-name">
                    {restaurant.name}
                  </span>
                  <span className="rounded-full bg-indigo-50 px-3 py-1 font-medium text-indigo-700" data-testid="restaurant-products-branch">
                    {restaurant.branch || "Sin sede"}
                  </span>
                  <span className="rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-700" data-testid="restaurant-products-erp">
                    A3: {restaurant.erp_customer_code || "Pendiente"}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700" data-testid="restaurant-products-address">
                    <MapPin className="h-3.5 w-3.5" />
                    {restaurant.address || "Dirección pendiente"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="restaurant-products-save-button"
          >
            <Save className="h-4 w-4" />
            {saving ? "Guardando..." : "Guardar catálogo"}
          </Button>
        </div>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="flex flex-col gap-3 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="font-medium text-blue-900" data-testid="restaurant-products-assigned-count">
                {assignedCount} productos asignados
              </p>
              <p className="text-sm text-blue-700">
                El precio final seguirá la prioridad: personalizado &gt; especial &gt; sede &gt; base.
              </p>
            </div>

            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Buscar por nombre, código o categoría"
                className="bg-white pl-10"
                data-testid="restaurant-products-search"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-5 w-5" />
              Productos disponibles ({filteredProducts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid gap-4 lg:grid-cols-2">
                {[...Array(6)].map((_, index) => (
                  <Skeleton key={index} className="h-48 w-full rounded-3xl" />
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="restaurant-products-empty-state">
                No se encontraron productos para este filtro.
              </div>
            ) : (
              <div className="grid gap-4 lg:grid-cols-2">
                {filteredProducts.map((product) => {
                  const assignment = normalizeAssignment(assignments[product.product_id]);
                  const resolvedBase = assignment.custom_price ?? product.price;
                  const discountedPrice = assignment.discount
                    ? (resolvedBase * (1 - assignment.discount / 100)).toFixed(2)
                    : null;

                  return (
                    <div
                      key={product.product_id}
                      className={`rounded-3xl border-2 p-5 transition-colors ${
                        assignment.assigned ? "border-blue-300 bg-blue-50/50" : "border-slate-200 bg-white"
                      }`}
                      data-testid={`restaurant-product-card-${product.product_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          role="button"
                          tabIndex={0}
                          className="flex flex-1 items-start gap-4 text-left"
                          onClick={() => toggleProduct(product.product_id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleProduct(product.product_id);
                            }
                          }}
                          data-testid={`toggle-product-${product.product_id}`}
                        >
                          <Checkbox checked={assignment.assigned} className="pointer-events-none mt-1" />

                          {product.image_url ? (
                            <img
                              src={resolveImageUrl(product.image_url)}
                              alt={product.name}
                              className="h-14 w-14 rounded-2xl object-cover"
                              loading="lazy"
                              data-testid={`product-image-${product.product_id}`}
                            />
                          ) : (
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                              <Package className="h-6 w-6" />
                            </div>
                          )}

                          <div className="min-w-0 flex-1 space-y-2">
                            <div>
                              <p className="font-semibold text-slate-900" data-testid={`product-name-${product.product_id}`}>
                                {product.name}
                              </p>
                              <p className="text-xs text-slate-400" data-testid={`product-meta-${product.product_id}`}>
                                {product.code || "Sin código"} · {product.category || "General"}
                              </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700" data-testid={`product-base-price-${product.product_id}`}>
                                Base: {product.price.toFixed(2)}€ / {product.unit}
                              </span>
                              {discountedPrice ? (
                                <span className="rounded-full bg-green-50 px-2.5 py-1 font-medium text-green-700" data-testid={`product-discounted-price-${product.product_id}`}>
                                  Final: {discountedPrice}€
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      {assignment.assigned && (
                        <>
                          <Separator className="my-4" />

                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <LabelText text="Descuento (%)" testId={`discount-label-${product.product_id}`} />
                              <div className="flex items-center gap-2">
                                <Tag className="h-4 w-4 text-green-600" />
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={assignment.discount}
                                  onChange={(event) => {
                                    const value = Number(event.target.value || 0);
                                    if (value >= 0 && value <= 100) {
                                      updateAssignmentField(product.product_id, "discount", value);
                                    }
                                  }}
                                  data-testid={`discount-input-${product.product_id}`}
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <LabelText text="Precio personalizado" testId={`custom-price-label-${product.product_id}`} />
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={assignment.custom_price ?? ""}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  updateAssignmentField(
                                    product.product_id,
                                    "custom_price",
                                    value === "" ? null : Number(value),
                                  );
                                }}
                                placeholder={product.price.toFixed(2)}
                                data-testid={`custom-price-input-${product.product_id}`}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function LabelText({ text, testId }) {
  return (
    <p className="text-sm font-medium text-slate-700" data-testid={testId}>
      {text}
    </p>
  );
}
